import {
  BlotterTable,
  Box,
  Button,
  ButtonVariants,
  Dialog,
  FileArea,
  HStack,
  Tooltip,
  useBlotterTable,
  type Column,
  type DialogProps,
  type MinimalSubscriptionResponse,
} from '@talos/kyoko';
import type { GridOptions, IsRowSelectable } from 'ag-grid-community';
import { isFunction } from 'lodash-es';
import { useCallback, useMemo, useRef, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import type { Observable } from 'rxjs';

export type ImportStep = 'initial' | 'uploading' | 'preview' | 'done';

export type ImportDialogDraft = {
  warning: string | undefined;
};

export type ImportDialogProps<TDraft extends ImportDialogDraft> = {
  onUpload: (file: File | undefined) => Promise<void>;
  /** If you want to provide your own component for handling the upload step, then pass that here. */
  customUploadComponent?: ReactElement;
  knowledgeBaseURL?: string;
  step: ImportStep;
} & PreviewProps<TDraft> &
  DialogProps;

export function ImportDialog<TDraft extends ImportDialogDraft>({
  title = 'Import',
  onUpload,
  onSelectionChanged,
  step,
  rowID,
  columns,
  dataObservable,
  knowledgeBaseURL,
  isRowSelectable,
  shouldPreselectRow,
  enableRowSelection,
  customUploadComponent,
  ...props
}: ImportDialogProps<TDraft>) {
  const { close, onCancel } = props;
  const handleCancel = useCallback(() => {
    onCancel?.();
    close();
  }, [onCancel, close]);

  return (
    <Dialog
      width={960}
      closeOnClickOutside={true}
      closeOnEscape={false}
      closeOnConfirm={false}
      showClose={true}
      customActions={
        <>
          <HStack
            justifyContent={knowledgeBaseURL ? 'space-between' : 'flex-end'}
            gap="spacing12"
            p="spacing16"
            w="100%"
            borderTop="1px solid var(--backgroundDivider)"
          >
            {knowledgeBaseURL ? (
              <Button forwardedAs={Link} to={knowledgeBaseURL}>
                Knowledge Base
              </Button>
            ) : null}
            <HStack justifyContent="flex-end" gap="spacing12">
              <Button onClick={handleCancel} data-testid="dialog-cancel">
                {props.cancelLabel}
              </Button>
              <Tooltip tooltip={props.confirmButtonTooltip}>
                <Button
                  variant={ButtonVariants.Primary}
                  onClick={props.onConfirm}
                  loading={props.confirmLoading}
                  disabled={props.confirmDisabled}
                  data-testid="dialog-confirm"
                >
                  {props.confirmLabel}
                </Button>
              </Tooltip>
            </HStack>
          </HStack>
        </>
      }
      title={title}
      {...props}
    >
      <Box h="400px" w="100%">
        {['initial', 'uploading'].includes(step) &&
          (customUploadComponent ?? <FileArea onChange={onUpload} accept="text/csv" />)}
        <Box mx="-spacing16">
          {['preview', 'done'].includes(step) && (
            <Preview
              dataObservable={dataObservable}
              columns={columns}
              rowID={rowID}
              onSelectionChanged={onSelectionChanged}
              isRowSelectable={isRowSelectable}
              shouldPreselectRow={shouldPreselectRow}
              enableRowSelection={enableRowSelection}
            />
          )}
        </Box>
      </Box>
    </Dialog>
  );
}

interface PreviewProps<TDraft extends ImportDialogDraft> {
  dataObservable: Observable<MinimalSubscriptionResponse<TDraft>>;
  columns: Column<TDraft>[];
  rowID: string;
  onSelectionChanged?: GridOptions<TDraft>['onSelectionChanged'];
  isRowSelectable?: IsRowSelectable<TDraft>;
  shouldPreselectRow?: boolean | ((row: TDraft) => boolean);
  /** Defaults to true. Set to false to not do anything with row selection in this preview. */
  enableRowSelection?: boolean;
}

function Preview<TDraft extends ImportDialogDraft>({
  dataObservable,
  columns,
  rowID,
  onSelectionChanged,
  isRowSelectable,
  shouldPreselectRow,
  enableRowSelection = true,
}: PreviewProps<TDraft>) {
  const didSelectAllRows = useRef(false);
  const rowSelection: GridOptions<TDraft>['rowSelection'] = useMemo(
    () => ({
      mode: 'multiRow',
      checkboxes: true,
      headerCheckbox: true,
      enableClickSelection: false,
      hideDisabledCheckboxes: true,
      isRowSelectable,
    }),
    [isRowSelectable]
  );
  const blotterTable = useBlotterTable({
    columns,
    rowID,
    dataObservable,
    errorObservable: undefined,
    gridOptions: {
      onSelectionChanged,
      rowSelection: enableRowSelection ? rowSelection : undefined,
      onRowDataUpdated: params => {
        if (didSelectAllRows.current) {
          return;
        }
        if (isFunction(shouldPreselectRow)) {
          const preselectedRowIDs: string[] = [];
          params.api.forEachNode(row => {
            if (row.data == null) {
              return;
            }
            if (shouldPreselectRow(row.data) && row.id != null) {
              preselectedRowIDs.push(row.id);
            }
          });
          blotterTable.selectRows(preselectedRowIDs);
        } else if (shouldPreselectRow) {
          blotterTable.selectAllRows();
        }
        didSelectAllRows.current = true;
      },
    },
  });

  return (
    <Box w="100%" h="400px" data-testid="import-dialog-preview-blotter">
      <BlotterTable {...blotterTable} background="backgroundModal" />
    </Box>
  );
}
